'use client';

import { useState, forwardRef } from 'react';
import { useTranslation } from '../lib/i18n';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test(value);
}

type EmailInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: string;
  showLabel?: boolean;
  /** When true, validation error shows even before blur (e.g. on submit attempt). */
  forceError?: boolean;
  /** Wrapper className for the label+input+error block. */
  wrapperClassName?: string;
};

const EmailInput = forwardRef<HTMLInputElement, EmailInputProps>(function EmailInput(
  { label, showLabel = true, forceError = false, wrapperClassName = '', className = '', value, onChange, onBlur, required, ...rest },
  ref
) {
  const { t } = useTranslation();
  const [touched, setTouched] = useState(false);
  const strValue = typeof value === 'string' ? value : '';
  const showError = (touched || forceError) && strValue.length > 0 && !isValidEmail(strValue);

  const handleInvalid = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    if (target.validity.valueMissing) {
      target.setCustomValidity(t('fillOutField'));
    } else if (target.validity.typeMismatch) {
      target.setCustomValidity(t('invalidEmailFormat'));
    } else {
      target.setCustomValidity('');
    }
  };

  return (
    <div className={wrapperClassName}>
      {showLabel && label && <label className="field-label">{label}{required && <span className="text-rust"> *</span>}</label>}
      <input
        ref={ref}
        type="email"
        className={`${className} ${showError ? 'field-input-invalid' : ''}`}
        value={value}
        onChange={(e) => {
          (e.target as HTMLInputElement).setCustomValidity('');
          onChange?.(e);
        }}
        onBlur={(e) => {
          setTouched(true);
          onBlur?.(e);
        }}
        required={required}
        onInvalid={handleInvalid}
        onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
        {...rest}
      />
      {showError && <p className="field-error-text">{t('invalidEmailFormat')}</p>}
    </div>
  );
});

export default EmailInput;
